import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@fmc-digital/i18n";
import { MFACodeContainerV2 } from "../../../components/organisms/MFACodeContainer/MFACodeContainerV2";
import { RegisterContext } from "../RegisterContext";
import { triggerOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/triggerOtp";
import { validateOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/validateOtp";
import { loadConfig } from "@/config/auth.config";
import {
  extractApiErrorCode,
  getRegisterErrorMessageFromCode,
  handleRegisterOtpValidationError,
  type RegisterOtpValidationResult,
} from "@/utils/registerErrorMessage";
import { trackRegistrationEvent, RegistrationEvent } from "@/utils/analytics.utils";

interface PhoneOtpStepProps {
  setStatusMessage: (msg: string | null) => void;
}

interface RegisterPhoneTriggerOtpRequest {
  factor: {
    factorType: "PHONE";
    phoneNumber: string;
    id: string;
  };
  existingFactor: boolean;
}

export function PhoneOtpStep({ setStatusMessage }: PhoneOtpStepProps) {
  const { t, i18n } = useTranslation();
  const {
    phone,
    otpId,
    otpFactorId,
    phoneFactorId,
    setPhoneFactorId,
    setOtpId,
    setOtp,
    goNext,
    goBack,
  } = useContext(RegisterContext);
  const [otpError, setOtpError] = useState<string | null>(null);
  const initialOtpRequestedRef = useRef(false);
  const triggerFactorIdRef = useRef("");

  const translate = (key: string) => t(key as never) as string;
  const normalizedPhone = useMemo(() => phone.replace(/\D/g, ""), [phone]);

  const trackPhoneOtpValidationFailed = (
    result: RegisterOtpValidationResult | undefined,
    errorCode: string | null,
  ) => {
    if (
      result?.status === "CLEAR_CODE" ||
      result?.status === "ALREADY_VALIDATED" ||
      result?.status === "BANNER_ONLY"
    ) {
      return;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.PHONE_OTP_VALIDATION_FAILED, {
        ...(errorCode ? { errorCode } : {}),
      });
    } catch {
      // Analytics must not block flow
    }
  };

  const triggerPhoneOtp = useCallback(async () => {
    setStatusMessage(null);
    let prevBaseUrl: string | undefined = undefined;

    if (!triggerFactorIdRef.current) {
      triggerFactorIdRef.current = otpId || "";
    }

    try {
      const config = await loadConfig();
      const baseUrlV2 = config.api.baseUrlV2 || config.api.baseUrl;
      if (typeof window !== "undefined" && window.__authShellConfig) {
        prevBaseUrl = window.__authShellConfig.apiBaseUrl;
        window.__authShellConfig.apiBaseUrl = `${baseUrlV2}/unauth`;
      }

      const payload: RegisterPhoneTriggerOtpRequest = {
        factor: {
          factorType: "PHONE",
          phoneNumber: normalizedPhone,
          id: triggerFactorIdRef.current,
        },
        existingFactor: false,
      };

      const response = await triggerOtp(payload);

      if (response?.id && response?.factorId) {
        setOtpId(response.id);
        setPhoneFactorId(response.factorId);
      } else {
        throw new Error("register:otp_trigger_failed");
      }
    } catch (err) {
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18n.exists.bind(i18n),
      );
      setStatusMessage(message);
      throw err;
    } finally {
      if (typeof window !== "undefined" && window.__authShellConfig && prevBaseUrl !== undefined) {
        window.__authShellConfig.apiBaseUrl = prevBaseUrl;
      }
    }
  }, [i18n, normalizedPhone, otpId, setOtpId, setPhoneFactorId, setStatusMessage, translate]);

  useEffect(() => {
    if (phoneFactorId || initialOtpRequestedRef.current) return;

    initialOtpRequestedRef.current = true;
    let mounted = true;

    const sendPhoneOtp = async () => {
      try {
        await triggerPhoneOtp();
        if (!mounted) return;
        try {
          trackRegistrationEvent(RegistrationEvent.PHONE_OTP_REQUEST_SENT);
        } catch {
          // Analytics must not block flow
        }
      } catch {
        if (!mounted) return;
        initialOtpRequestedRef.current = false;
      }
    };

    sendPhoneOtp();

    return () => {
      mounted = false;
    };
  }, [phoneFactorId, triggerPhoneOtp]);

  const handleValidate = async (code: string) => {
    setStatusMessage(null);
    setOtpError(null);

    const factorId = phoneFactorId || otpFactorId;
    if (!otpId || !factorId) {
      setOtpError("Something went wrong.");
      return null;
    }

    try {
      trackRegistrationEvent(RegistrationEvent.PHONE_OTP_SUBMITTED);
    } catch {
      // Analytics must not block validation
    }

    try {
      return await validateOtp({
        factorValue: code,
        id: otpId,
        factorId,
      });
    } catch (err) {
      const errorCode = extractApiErrorCode(err);
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18n.exists.bind(i18n),
      );

      const result = handleRegisterOtpValidationError(
        errorCode,
        message,
        setStatusMessage,
        setOtpError,
      );
      trackPhoneOtpValidationFailed(result, errorCode);
      return result;
    }
  };

  const handleVerify = (verifiedCode: string) => {
    setStatusMessage(null);
    try {
      trackRegistrationEvent(RegistrationEvent.PHONE_OTP_VALIDATION_SUCCESS);
    } catch {
      // Analytics must not block navigation
    }
    setOtp(verifiedCode);
    goNext();
  };

  const handleInputEdit = () => {
    if (otpError) setOtpError(null);
    setStatusMessage(null);
  };

  const handleCancel = () => {
    setOtpError(null);
    setStatusMessage(null);
    goBack();
  };

  const handleResend = async () => {
    setOtpError(null);
    await triggerPhoneOtp();
  };

  return (
    <MFACodeContainerV2
        factorState={{
          factorType: "sms",
          profile: { phoneNumber: normalizedPhone },
        }}
        onVerify={handleVerify}
        onCancel={handleCancel}
        onValidate={handleValidate}
        onResend={handleResend}
        inputError={otpError}
        onInputEdit={handleInputEdit}
    />
  );
}
